'use client';

import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { SEED_ENQUIRIES, ACTIVE_COSTUME, ORG_SOURCES } from "./data";
import { hostData as mockHostData } from "./hostData";
import { createMemoryStorage } from "./storage";
import { withHostCache } from "./hostCache";
import { normalizePhone } from "./parts";

const StateCtx = createContext(null);

// Fallback colours for custom stages an org adds (built-in stage keys keep
// their costume colour). Deterministic by key so a stage's colour is stable
// regardless of where it sits in the pipeline.
const STAGE_PALETTE = [
  "bg-sky-100 text-sky-800 border-sky-200",
  "bg-amber-100 text-amber-800 border-amber-200",
  "bg-purple-100 text-purple-800 border-purple-200",
  "bg-teal-100 text-teal-800 border-teal-200",
  "bg-orange-100 text-orange-800 border-orange-200",
  "bg-indigo-100 text-indigo-800 border-indigo-200",
];
const stagePalette = (key) => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return STAGE_PALETTE[h % STAGE_PALETTE.length];
};

export const StateProvider = ({
  children,
  storage,            // storage adapter; defaults to in-memory demo storage
  hostData,           // host read-only window (contract §2.4); defaults to demo mocks
  costume,            // resolved costume object; defaults to ACTIVE_COSTUME
  onConverted,        // host write callback (contract §2.3); optional in demo
  currentUser,        // host auth user (contract §2.2); demo default "Anita"
  companyInfo,        // org identity for header + WhatsApp templates; defaults to costume's
  lookupTrigger = "auto", // 'auto' (fires at 10 digits) | 'button' (explicit Search, phone-first layout)
  demoMode = false,   // demo shell sets true: tags new records "(DEMO)"
  enableLines = true, // host gate: when false (e.g. Solo plan), hide the line-switcher / "add a business"
  teamMembers = null, // host-provided [{user_id, name, role}] — enables the "Handled by" reassign control (null = feature off)
  onError,            // optional error callback (no console noise per host constraints)
}) => {
  const activeCostume = costume || ACTIVE_COSTUME;
  const storeRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = storage || createMemoryStorage(SEED_ENQUIRIES, { sources: ORG_SOURCES });
  }
  const store = storeRef.current;
  const host = useMemo(() => withHostCache(hostData || mockHostData), [hostData]);
  const me = currentUser?.name || "Anita";
  const myRole = currentUser?.role || "owner";
  const info = companyInfo || activeCostume.companyInfo;

  const [enquiries, setEnquiries] = useState([]);
  const [orgSettings, setOrgSettings] = useState({ sources: [], enquiry_types: [], lost_reasons: [], stages: [], lines: [] });
  const [loaded, setLoaded] = useState(false);
  const [activeLineId, setActiveLineId] = useState(null);

  const stageColor = useCallback(
    (key) => activeCostume.stageColors?.[key] || stagePalette(key),
    [activeCostume]
  );

  // ----- Lines of business -----
  // Most orgs run a single sales journey. Some (e.g. Aumonline: Software +
  // Mutual funds) run several, each with its OWN statuses / types / lost
  // reasons. We model that as "lines". When none are saved the org has ONE
  // implicit default line built from its org-level settings (or the costume),
  // so single-line orgs behave exactly as before.
  const fallbackStages = orgSettings.stages?.length ? orgSettings.stages : activeCostume.stages;
  const fallbackTypes = orgSettings.enquiry_types?.length ? orgSettings.enquiry_types : activeCostume.enquiryTypes;
  const fallbackLost = orgSettings.lost_reasons?.length ? orgSettings.lost_reasons : activeCostume.lostReasons;
  const lines = (orgSettings.lines && orgSettings.lines.length)
    ? orgSettings.lines
    : [{ id: "default", name: "Sales", stages: fallbackStages, enquiry_types: fallbackTypes, lost_reasons: fallbackLost }];
  const activeLine = lines.find((l) => l.id === activeLineId) || lines[0];
  const resolvedStages = activeLine.stages;

  // Ref mirror so callbacks can read current data without re-creating.
  const enquiriesRef = useRef(enquiries);
  useEffect(() => { enquiriesRef.current = enquiries; }, [enquiries]);

  const fail = useCallback((code, cause, context = {}) => {
    // Structured error reporting (Round-trip 6, Q3): onError always
    // receives { code, message, context } — never a bare Error.
    if (!onError) return;
    onError({
      code,
      message: (cause && cause.message) || String(cause || "Unknown error"),
      context: { ...context, cause },
    });
  }, [onError]);

  useEffect(() => {
    let alive = true;
    Promise.all([store.loadEnquiries(), store.loadOrgSettings()])
      .then(([es, os]) => {
        if (!alive) return;
        setEnquiries(es);
        // Fall back to costume defaults when the org has no saved list yet.
        setOrgSettings({
          ...os,
          sources: os.sources?.length ? os.sources : activeCostume.defaultSources,
          enquiry_types: os.enquiry_types?.length ? os.enquiry_types : activeCostume.enquiryTypes,
          lost_reasons: os.lost_reasons?.length ? os.lost_reasons : activeCostume.lostReasons,
          stages: os.stages?.length ? os.stages : activeCostume.stages,
          lines: os.lines || [],
        });
        setLoaded(true);
      })
      .catch((err) => fail("LOAD_FAILED", err, { op: "initialLoad" }));
    return () => { alive = false; };
  }, [store, activeCostume, fail]);

  const updateSources = useCallback((sources) => {
    setOrgSettings((s) => ({ ...s, sources }));
    store.updateOrgSettings({ sources }).catch((err) =>
      fail("WRITE_FAILED", err, { op: "updateOrgSettings" })
    );
  }, [store, fail]);

  const persistLines = useCallback((nextLines) => {
    setOrgSettings((s) => ({ ...s, lines: nextLines }));
    store.updateOrgSettings({ lines: nextLines }).catch((err) =>
      fail("WRITE_FAILED", err, { op: "updateOrgSettings" })
    );
  }, [store, fail]);

  // Editing any line materialises the implicit default into a real saved list.
  const updateLine = useCallback((lineId, patch) => {
    persistLines(lines.map((l) => (l.id === lineId ? { ...l, ...patch } : { ...l })));
  }, [lines, persistLines]);

  const addLine = useCallback((name) => {
    const id = `line_${Date.now().toString(36)}`;
    const fresh = {
      id, name,
      stages: activeCostume.stages.map((s) => ({ ...s })),
      enquiry_types: [...activeCostume.enquiryTypes],
      lost_reasons: [...activeCostume.lostReasons],
    };
    persistLines([...lines.map((l) => ({ ...l })), fresh]);
    setActiveLineId(id);
  }, [lines, persistLines, activeCostume]);

  const renameLine = useCallback((lineId, name) => updateLine(lineId, { name }), [updateLine]);

  const removeLine = useCallback((lineId) => {
    if (lines.length <= 1) return;
    const next = lines.filter((l) => l.id !== lineId).map((l) => ({ ...l }));
    persistLines(next);
    setActiveLineId((cur) => (cur === lineId ? next[0].id : cur));
  }, [lines, persistLines]);

  const updateEnquiryTypes = useCallback((enquiry_types) => updateLine(activeLine.id, { enquiry_types }), [updateLine, activeLine.id]);

  const updateLostReasons = useCallback((lost_reasons) => updateLine(activeLine.id, { lost_reasons }), [updateLine, activeLine.id]);

  const updateStages = useCallback((stages) => updateLine(activeLine.id, { stages }), [updateLine, activeLine.id]);

  const addEnquiry = useCallback((e) => {
    const rec = {
      id: crypto.randomUUID(),
      stage: "new",
      created_at: new Date().toISOString(),
      assigned_to: me,
      ...e,
    };
    // New leads belong to the line of business the operator is viewing.
    if (!rec.line_id) rec.line_id = activeLine.id;
    // Every new lead must enter the follow-up loop immediately — default it
    // to "contact today" so it surfaces in the operator's due list and is
    // never invisible (overridden the moment they set a real next date).
    if (!rec.next_followup_date) {
      const t = new Date();
      t.setHours(9, 0, 0, 0);
      rec.next_followup_date = t.toISOString();
    }
    setEnquiries((prev) => [rec, ...prev]);
    store.createEnquiry(rec).catch((err) =>
      fail("WRITE_FAILED", err, { op: "createEnquiry", enquiryId: rec.id })
    );
  }, [store, me, fail, activeLine.id]);

  const updateEnquiry = useCallback((id, patch) => {
    setEnquiries((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    store.updateEnquiry(id, patch).catch((err) =>
      fail("WRITE_FAILED", err, { op: "updateEnquiry", enquiryId: id })
    );
  }, [store, fail]);

  // Operator remark — free text, auto-stamped with time + current stage.
  // System remarks (audit log entries written by the engine, e.g. "Edited
  // facts: phone, email") flow through the same function so the timeline
  // stays a single ordered list.
  const addRemark = useCallback((id, text, opts = {}) => {
    const cur = enquiriesRef.current.find((x) => x.id === id);
    if (!cur) return;
    const remark = {
      id: crypto.randomUUID(),
      text,
      at: new Date().toISOString(),
      stage: cur.stage,
      by: me,
      system: !!opts.system,
    };
    setEnquiries((prev) =>
      prev.map((x) => (x.id === id ? { ...x, remarks: [...(x.remarks || []), remark] } : x))
    );
    store.addRemark(id, remark).catch((err) =>
      fail("WRITE_FAILED", err, { op: "addRemark", enquiryId: id })
    );
  }, [store, me, fail]);

  const changeStage = useCallback((id, newStage, extra = {}) => {
    // Stage changes auto-log into the remarks timeline (system entry).
    const label = resolvedStages.find((s) => s.key === newStage)?.label || newStage;
    const sysRemark = {
      id: crypto.randomUUID(),
      text: `Moved to ${label}`,
      at: new Date().toISOString(),
      stage: newStage,
      by: me,
      system: true,
    };
    const cur = enquiriesRef.current.find((x) => x.id === id);
    // Remember where it came from so a single tap can undo the move
    // (the conversion-critical "nothing is a dead-end" rule).
    const patch = { stage: newStage, prev_stage: cur?.stage ?? null, ...extra };
    if (newStage === "signed_up") {
      if (cur && !cur.signed_up_at) patch.signed_up_at = new Date().toISOString();
    }
    setEnquiries((prev) =>
      prev.map((x) =>
        x.id === id ? { ...x, ...patch, remarks: [...(x.remarks || []), sysRemark] } : x
      )
    );
    store.updateEnquiry(id, patch).catch((err) =>
      fail("WRITE_FAILED", err, { op: "updateEnquiry", enquiryId: id })
    );
    store.addRemark(id, sysRemark).catch((err) =>
      fail("WRITE_FAILED", err, { op: "addRemark", enquiryId: id })
    );
  }, [activeCostume, resolvedStages, store, me, fail]);

  // One-tap undo of the last stage move (including an accidental Won / Lost).
  // Reverts to the stage it came from, clears the Won/Lost side-fields, and
  // leaves an audit trail. No dead-ends: every move is reversible.
  const undoStage = useCallback((id) => {
    const cur = enquiriesRef.current.find((x) => x.id === id);
    if (!cur || !cur.prev_stage) return;
    const back = cur.prev_stage;
    const label = resolvedStages.find((s) => s.key === back)?.label || back;
    const patch = { stage: back, prev_stage: null };
    if (cur.stage === "lost") patch.lost_reason = null;
    if (cur.stage === "signed_up") patch.signed_up_at = null;
    const sysRemark = {
      id: crypto.randomUUID(),
      text: `Undid the last change — moved back to ${label}`,
      at: new Date().toISOString(),
      stage: back,
      by: me,
      system: true,
    };
    setEnquiries((prev) =>
      prev.map((x) => (x.id === id ? { ...x, ...patch, remarks: [...(x.remarks || []), sysRemark] } : x))
    );
    store.updateEnquiry(id, patch).catch((err) =>
      fail("WRITE_FAILED", err, { op: "updateEnquiry", enquiryId: id })
    );
    store.addRemark(id, sysRemark).catch((err) =>
      fail("WRITE_FAILED", err, { op: "addRemark", enquiryId: id })
    );
  }, [resolvedStages, store, me, fail]);

  // Move a lead to a different line of business. Resets it to that line's
  // first stage (a fresh start there), optionally re-picks its type, and
  // keeps its full history/remarks. Also unblocks deleting a line that
  // still held leads. Not treated as an undoable stage move.
  const moveEnquiry = useCallback((id, targetLineId, enquiryType) => {
    const cur = enquiriesRef.current.find((x) => x.id === id);
    if (!cur) return;
    const fromLine = lines.find((l) => l.id === (cur.line_id || "default"));
    const toLine = lines.find((l) => l.id === targetLineId);
    const patch = { line_id: targetLineId, stage: "new", prev_stage: null };
    if (enquiryType) patch.enquiry_type = enquiryType;
    const sysRemark = {
      id: crypto.randomUUID(),
      text: `Moved from "${fromLine?.name || "—"}" to "${toLine?.name || "—"}" — reset to the first step`,
      at: new Date().toISOString(),
      stage: "new",
      by: me,
      system: true,
    };
    setEnquiries((prev) =>
      prev.map((x) => (x.id === id ? { ...x, ...patch, remarks: [...(x.remarks || []), sysRemark] } : x))
    );
    store.updateEnquiry(id, patch).catch((err) =>
      fail("WRITE_FAILED", err, { op: "updateEnquiry", enquiryId: id })
    );
    store.addRemark(id, sysRemark).catch((err) =>
      fail("WRITE_FAILED", err, { op: "addRemark", enquiryId: id })
    );
  }, [lines, store, me, fail]);

  // Permanently delete a lead (UI gates this behind a confirm).
  const deleteEnquiry = useCallback((id) => {
    setEnquiries((prev) => prev.filter((x) => x.id !== id));
    store.deleteEnquiry(id).catch((err) =>
      fail("WRITE_FAILED", err, { op: "deleteEnquiry", enquiryId: id })
    );
  }, [store, fail]);

  // Conversion handback — contract §2.3. Builds the locked payload, awaits
  // the host. On host error the lead stays pre-conversion so the operator
  // can retry (locked with Kreta, 10 Jun 2026).
  // `prePatch` (Round-trip 6, Q1): location chosen at conversion time is
  // applied to the record before the payload is built.
  const convertEnquiry = useCallback(async (id, prePatch = null) => {
    const base = enquiriesRef.current.find((x) => x.id === id);
    if (!base) return { ok: false, error: "Enquiry not found." };
    if (prePatch) updateEnquiry(id, prePatch);
    const e = { ...base, ...(prePatch || {}) };
    if (activeCostume.buttonsOn.liveStock && !e.location_id) {
      // UI should have prompted via the location picker before calling.
      return { ok: false, error: `Pick a ${activeCostume.vocab.location} before converting.` };
    }
    const payload = {
      enquiryId: e.id,
      name: e.name,
      phone: normalizePhone(e.phone), // 10 digits, no country code (contract §4)
      email: e.email || null,
      enquiry_type: activeCostume.enquiryTypeHostEnum?.[e.enquiry_type] || e.enquiry_type,
      seat_count: e.seat_count || null,
      location_id: e.location_id,    // target hub (Round-trip 6, Q1)
      source: e.source,
      notes: e.notes || null,
    };
    let res;
    try {
      res = onConverted ? await onConverted(e.id, payload) : {};
    } catch (err) {
      fail("CONVERT_HANDBACK_FAILED", err, { op: "onConverted", enquiryId: e.id });
      return { ok: false, error: "Could not hand over to the host system. The lead is unchanged — try again." };
    }
    if (res && res.error) {
      return { ok: false, error: res.error };
    }
    changeStage(id, "signed_up", {
      deal_value: res?.deal_value ?? e.deal_value ?? null,
      host_account_id: res?.host_account_id ?? null,
      closed_by: me,
    });
    return { ok: true, deal_value: res?.deal_value ?? null };
  }, [activeCostume, onConverted, changeStage, updateEnquiry, me, fail]);

  const lineEnquiries = useMemo(
    () => enquiries.filter((e) => (e.line_id || "default") === activeLine.id),
    [enquiries, activeLine.id]
  );

  const value = useMemo(
    () => ({
      enquiries, lineEnquiries, loaded,
      addEnquiry, updateEnquiry, changeStage, addRemark, convertEnquiry,
      undoStage, moveEnquiry, deleteEnquiry,
      orgSettings, updateSources, updateEnquiryTypes, updateLostReasons, updateStages,
      lines, activeLine, activeLineId: activeLine.id, setActiveLine: setActiveLineId,
      addLine, renameLine, removeLine,
      enquiryTypes: activeLine.enquiry_types?.length ? activeLine.enquiry_types : activeCostume.enquiryTypes,
      lostReasons: activeLine.lost_reasons?.length ? activeLine.lost_reasons : activeCostume.lostReasons,
      stages: resolvedStages, stageColor,
      costume: activeCostume, hostData: host,
      me, myRole, meId: currentUser?.id || null, teamMembers,
      companyInfo: info, demoMode, lookupTrigger, enableLines,
    }),
    [enquiries, lineEnquiries, loaded, addEnquiry, updateEnquiry, changeStage, addRemark, convertEnquiry,
     undoStage, moveEnquiry, deleteEnquiry,
     orgSettings, updateSources, updateEnquiryTypes, updateLostReasons, updateStages,
     lines, activeLine, addLine, renameLine, removeLine,
     resolvedStages, stageColor, activeCostume, host, me, myRole, currentUser, teamMembers, info, demoMode, lookupTrigger, enableLines]
  );

  return <StateCtx.Provider value={value}>{children}</StateCtx.Provider>;
};

export const useJigyasa = () => {
  const ctx = useContext(StateCtx);
  if (!ctx) throw new Error("useJigyasa must be used inside StateProvider");
  return ctx;
};

export const useCostume = () => useJigyasa().costume;

// ---------- derived helpers ----------

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

export const sameDay = (a, b) => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

export const isOverdue = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const today = new Date();
  d.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
};

export const daysLate = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  d.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
};

export const filterCallBackToday = (enquiries) =>
  enquiries
    .filter(
      (e) =>
        e.stage !== "signed_up" &&
        e.stage !== "lost" &&
        e.next_followup_date &&
        isOverdue(e.next_followup_date)
    )
    .sort(
      (a, b) =>
        new Date(a.next_followup_date).getTime() -
        new Date(b.next_followup_date).getTime()
    );

// The one scheduled-appointment stage across every costume (engine keys are
// fixed; only labels change — "Site visit scheduled" / "Demo scheduled" / …).
export const APPOINTMENT_STAGE = "visit_scheduled";

export const isLiveStage = (stage) => stage !== "signed_up" && stage !== "lost";

// Date-only "is this today or earlier?" (drives the due-today lists).
export const isDueOrOverdue = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const today = new Date();
  d.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return d.getTime() <= today.getTime();
};

// A live lead with no next date is an invisible lead — the guard flags it.
export const needsNextDate = (e) => isLiveStage(e.stage) && !e.next_followup_date;

const byNextDate = (a, b) =>
  new Date(a.next_followup_date).getTime() - new Date(b.next_followup_date).getTime();

// Calls to make today/overdue: live, NOT a scheduled appointment, date due.
export const filterCallsDue = (enquiries) =>
  enquiries
    .filter(
      (e) =>
        isLiveStage(e.stage) &&
        e.stage !== APPOINTMENT_STAGE &&
        e.next_followup_date &&
        isDueOrOverdue(e.next_followup_date)
    )
    .sort(byNextDate);

// Demos & visits today/overdue: leads in the scheduled-appointment stage.
export const filterAppointmentsDue = (enquiries) =>
  enquiries
    .filter(
      (e) =>
        e.stage === APPOINTMENT_STAGE &&
        e.next_followup_date &&
        isDueOrOverdue(e.next_followup_date)
    )
    .sort(byNextDate);

// Demos & visits planning window: overdue + today + the next `days` days.
// A demo/visit is with the client and needs travel/planning, so it must
// surface well before the day itself (owner rule: 7 days ahead).
export const filterAppointmentsUpcoming = (enquiries, days = 7) => {
  const horizon = new Date();
  horizon.setHours(0, 0, 0, 0);
  horizon.setDate(horizon.getDate() + days);
  return enquiries
    .filter((e) => {
      if (e.stage !== APPOINTMENT_STAGE || !e.next_followup_date) return false;
      const d = new Date(e.next_followup_date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() <= horizon.getTime();
    })
    .sort(byNextDate);
};

export const filterNewToday = (enquiries) =>
  enquiries.filter((e) => sameDay(e.created_at, new Date()));

export const filterByStage = (enquiries, stage) =>
  enquiries.filter((e) => e.stage === stage);

// ---------- owner dashboard derivations ----------

export const ownerMetrics = (enquiries) => {
  const now = new Date();
  const thisMonth = startOfMonth(now);
  const lastMonth = startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  const inMonth = (iso, start) => {
    if (!iso) return false;
    const d = new Date(iso);
    const next = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    return d >= start && d < next;
  };

  const newThisList = enquiries
    .filter((e) => inMonth(e.created_at, thisMonth))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const newThisMonth = newThisList.length;
  const signedThisMonth = enquiries.filter(
    (e) => e.stage === "signed_up" && inMonth(e.signed_up_at, thisMonth)
  );
  const signedThisList = [...signedThisMonth].sort(
    (a, b) => new Date(b.signed_up_at).getTime() - new Date(a.signed_up_at).getTime()
  );
  const valueThisMonth = signedThisMonth.reduce((s, e) => s + (e.deal_value || 0), 0);

  const newLastMonth = enquiries.filter((e) => inMonth(e.created_at, lastMonth)).length;
  const signedLastMonth = enquiries.filter(
    (e) => e.stage === "signed_up" && inMonth(e.signed_up_at, lastMonth)
  );
  const valueLastMonth = signedLastMonth.reduce((s, e) => s + (e.deal_value || 0), 0);

  // 6-month dual trend: enquiries received vs signed up, per month.
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const bars = [];
  for (let i = 5; i >= 0; i--) {
    const start = startOfMonth(new Date(now.getFullYear(), now.getMonth() - i, 1));
    const label = monthNames[start.getMonth()];
    const enquiriesCount = enquiries.filter((e) => inMonth(e.created_at, start)).length;
    const signed = enquiries.filter(
      (e) => e.stage === "signed_up" && inMonth(e.signed_up_at, start)
    ).length;
    bars.push({ month: label, enquiries: enquiriesCount, signed });
  }

  // donuts — last 30 days
  const thirtyAgo = new Date();
  thirtyAgo.setDate(thirtyAgo.getDate() - 30);

  const sourceCounts = {};
  enquiries
    .filter((e) => new Date(e.created_at) >= thirtyAgo)
    .forEach((e) => {
      sourceCounts[e.source] = (sourceCounts[e.source] || 0) + 1;
    });
  const sourceData = Object.entries(sourceCounts).map(([name, value]) => ({ name, value }));

  const lostCounts = {};
  enquiries
    .filter((e) => e.stage === "lost" && new Date(e.created_at) >= thirtyAgo)
    .forEach((e) => {
      const k = e.lost_reason || "Other";
      lostCounts[k] = (lostCounts[k] || 0) + 1;
    });
  const lostData = Object.entries(lostCounts).map(([name, value]) => ({ name, value }));

  const recentWins = enquiries
    .filter((e) => e.stage === "signed_up")
    .sort((a, b) => new Date(b.signed_up_at).getTime() - new Date(a.signed_up_at).getTime())
    .slice(0, 5);

  return {
    newThisMonth,
    signedCountThis: signedThisMonth.length,
    valueThisMonth,
    newLastMonth,
    signedCountLast: signedLastMonth.length,
    valueLastMonth,
    bars,
    sourceData,
    lostData,
    recentWins,
    newThisList,
    signedThisList,
  };
};

// format ₹ as Indian-style (lakhs)
export const formatINR = (n) => {
  if (n == null) return "—";
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}k`;
  return `₹${n}`;
};


// ---------- Button 4 (Jigyasa-side) — past enquiry lookup ----------
// Find prior enquiries from the same phone (excluding the current one).
export const findPastEnquiries = (enquiries, currentId, phone) => {
  if (!phone) return [];
  const norm = normalizePhone(phone);
  return enquiries
    .filter((e) => e.id !== currentId && normalizePhone(e.phone) === norm)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};

// ---------- Smart suggestions (button 7 derivatives + lapsed-lead pairing) ----------
// Build the auto-task list shown on the operator home: expansion radar + lapsed-lead resurrection.
export const buildSmartSuggestions = ({ enquiries, renewalPipeline, upcomingStock }) => {
  const out = [];

  // Expansion radar — renewals with a growing signal.
  (renewalPipeline?.renewals || [])
    .filter((r) => r.growthSignal && r.growthSignal.score > 0)
    .forEach((r) => {
      out.push({
        kind: "expansion",
        title: `${r.customerName} — likely outgrowing their plan`,
        body: `Lease ends in ${r.daysUntilEnd} days. ${r.growthSignal.narrative}`,
        actionLabel: "Open renewal conversation",
        meta: { customerName: r.customerName, daysUntilEnd: r.daysUntilEnd },
      });
    });

  // Lapsed-lead resurrection — pair upcoming vacancies with old lost enquiries.
  const lostEnquiries = enquiries.filter((e) => e.stage === "lost");
  (upcomingStock?.events || [])
    .filter((ev) => ev.type === "vacancy")
    .forEach((ev) => {
      const match = lostEnquiries.find((e) => e.seat_count && e.seat_count <= ev.unitCount + 1 && e.seat_count >= ev.unitCount - 1);
      if (match) {
        out.push({
          kind: "lapsed",
          title: `${ev.locationName} ${ev.subLocation || ""} — ${ev.unitCount} seats freeing ${ev.effectiveDate}`,
          body: `Three months ago ${match.name} was lost (${match.lost_reason}). They wanted ${match.seat_count} seats — same size as what's about to free up.`,
          actionLabel: `Call ${match.name.replace(" (DEMO)", "")}`,
          meta: { enquiryId: match.id, locationName: ev.locationName },
        });
      }
    });

  return out;
};

// ---------- Owner capacity snapshot helper ----------
export const buildCapacitySnapshot = (liveStock) => {
  if (!liveStock) return null;
  const pct = Math.round((liveStock.totalOccupied / liveStock.totalCapacity) * 100);
  return {
    totalCapacity: liveStock.totalCapacity,
    totalOccupied: liveStock.totalOccupied,
    totalFree: liveStock.totalFree,
    occupancyPct: pct,
    locations: liveStock.locations.map((l) => ({
      name: l.locationName,
      pct: l.occupancyPct,
      free: l.free,
    })),
  };
};
